import React, { useCallback, useState } from 'react';

import { Icon, message, Popconfirm, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Document } from 'react-pdf';

import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { handleThumbnailError, INSERT_NAMESPACES } from '../../../utils/insertsHelper';
import { InsertPageWrapper, PreviewInsertContainer } from './styles';
import { PageActionItem, PageMetaDetails, PageNumber } from '../styles';
import {
  updateInsertBusinessLogic,
  updateTemplateInsert,
} from '../../../utils/services/insertsApi';
import { deepCopy } from '../../../utils/javascript';
import { BusinessLogicOptionPopover, InsertBusinessLogicModal } from './InsertsBusinessLogic';
import { InsertPreviewModal, PreviewPageWithObserver } from './InsertPreviewModal';
import { INSERTS_TAB } from './translationValues';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { COMPLEX_DOCUMENT_VERSION, dummyCallback } from '../../../utils/complexDocumentConstants';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';

const PreviewModal = ({ insert, totalPages, scaling, currentStartPageIndex }) => {
  const { url } = insert;
  const [numOfPages, setNumOfPages] = useState(0);

  return (
    <PreviewInsertContainer>
      <Document
        file={url}
        onLoadSuccess={({ numPages }) => setNumOfPages(numPages)}
        loading={
          <div style={{ width: 635, height: 899, background: '#fff', justifySelf: 'center' }}></div>
        }
        error=""
        onLoadError={error => console.error({ error })}
        externalLinkTarget={'_blank'}
        onItemClick={dummyCallback}
      >
        {Array.from(new Array(numOfPages), (_, index) => (
          <div
            key={`page_preview${index + 1}`}
            className="page_preview_pageLayout"
            id={`page_preview_pdfPageNumber-${index + 1}`}
          >
            <PageMetaDetails
              rulerEnabled={false}
              rulerAdjusted={true}
              dndFlow={false}
              currentDesign={'document'}
            >
              <PageNumber
                style={{ fontSize: `${scaling < 0.3 ? Math.max(scaling * 16, 8) : 16}px` }}
              >
                {`Page ${currentStartPageIndex + index + 1} of ${totalPages}`}
                {/* {t(COMPLEX_PAGE_OPTIONS.PAGE_HEADER_TEXT.key, COMPLEX_PAGE_OPTIONS.PAGE_HEADER_TEXT.value, { index: flowNumber + 1, totalPages })} */}
              </PageNumber>
            </PageMetaDetails>
            <PreviewPageWithObserver
              key={`page_preview-${index + 1}`}
              pageNumber={index + 1}
              setPageVisibility={() => {}}
              type="preview"
            />
          </div>
        ))}
      </Document>
    </PreviewInsertContainer>
  );
};

const PdfInsert = ({
  insert,
  templateInserts,
  totalInserts,
  templateId,
  globalVariables,
  store,
  editable,
  isPreviewModal,
  totalPages,
  scaling,
  currentStartPageIndex,
  t,
  isInteractiveDesign,
}) => {
  const { thumbnail, name, orderNumber, businessLogic } = insert;

  const [showBlModal, setShowBlModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [, updateArtboard] = useArtboardContext(store => null, ''); 
  
  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, `PdfInsert`);

  const handleDelete = async () => {
    let count = 1;
    const tempInserts = deepCopy(templateInserts).map(tInsert => {
      const isDeleted = tInsert.relationId === insert.relationId;
      const res = {
        ...tInsert,
        orderNumber: isDeleted ? orderNumber : count,
        isDeleted: isDeleted,
      };
      if (!isDeleted) {
        count++;
      }
      return res;
    });
    const payload = tempInserts.map(({ orderNumber, id, relationId, isDeleted }) => ({
      orderNumber,
      id,
      relationId,
      isDeleted,
    }));
    try {
      const response = await updateTemplateInsert(templateId, payload);
      if (response?.statusCode === 133) {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: tempInserts.filter(
            ({ isDeleted }) => !isDeleted,
          ),
        });
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
          })
        });
        message.success(
          t(INSERTS_TAB.INSERT_DELETE_SUCCESS.key, INSERTS_TAB.INSERT_DELETE_SUCCESS.value),
        );
      } else {
        message.error(
          t(INSERTS_TAB.INSERT_DELETE_FAILURE.key, INSERTS_TAB.INSERT_DELETE_FAILURE.value),
        );
      }
    } catch (e) {
      message.error(
        t(INSERTS_TAB.INSERT_DELETE_FAILURE.key, INSERTS_TAB.INSERT_DELETE_FAILURE.value),
      );
    }
  };

  const swapInserts = async targetOrderNumber => {
    const currentIndex = orderNumber - 1;
    const targetIndex = targetOrderNumber - 1;
    const tempInserts = deepCopy(templateInserts);
    const currentValue = tempInserts[currentIndex];
    const targetValue = tempInserts[targetIndex];
    targetValue.orderNumber = orderNumber;
    currentValue.orderNumber = targetOrderNumber;
    tempInserts[currentIndex] = targetValue;
    tempInserts[targetIndex] = currentValue;
    const payload = tempInserts.map(({ orderNumber, id, relationId }) => ({
      orderNumber,
      id,
      relationId,
    }));
    try {
      const response = await updateTemplateInsert(templateId, payload);
      if (response?.statusCode === 133) {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: tempInserts,
        });
      } else {
        message.error(
          t(INSERTS_TAB.INSERT_SWAP_FAILURE.key, INSERTS_TAB.INSERT_SWAP_FAILURE.value),
        );
      }
    } catch (e) {
      message.error(t(INSERTS_TAB.INSERT_SWAP_FAILURE.key, INSERTS_TAB.INSERT_SWAP_FAILURE.value));
    }
  };

  const deleteBusinessLogic = async cb => {
    const payload = {
      relationId: insert.relationId,
      businessLogic: { ...businessLogic, isDeleted: true },
    };
    try {
      const response = await updateInsertBusinessLogic(payload);
      if (response?.statusCode === 132) {
        const tempInserts = templateInserts.map(tInsert => {
          if (tInsert.relationId === insert.relationId) {
            return { ...tInsert, businessLogic: undefined };
          }
          return tInsert;
        });
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: tempInserts });
        cb && cb();
      }
    } catch (e) {}
  };

  const toggleBusinessLogicModal = useCallback(()=> {
    updateArtboard({[CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]: { logicType: 'inserts', insert:insert }});
  },[insert]);

  return (
    <InsertPageWrapper>
      <div className="pageLayout">
        {editable || isInteractiveDesign ? (
          <div className="top-tools">
            <div>
              {insert.pages === 1
                ? t(INSERTS_TAB.PAGES_LABEL.key, INSERTS_TAB.PAGES_LABEL.value, {
                    count: insert.pages,
                  })
                : t(INSERTS_TAB.PAGES_LABEL_PLURAL.key, INSERTS_TAB.PAGES_LABEL_PLURAL.value, {
                    count: insert.pages,
                  })}
            </div>
            <div style={{ display: 'flex' }}>
              {!isInteractiveDesign ? (
                <>
                  {orderNumber !== 1 ? (
                    <PageActionItem onClick={() => swapInserts(orderNumber - 1)}>
                      <Tooltip
                        placement="bottom"
                        title={t(
                          INSERTS_TAB.INSERT_SWAP_PREV.key,
                          INSERTS_TAB.INSERT_SWAP_PREV.value,
                        )}
                      >
                        <Icon type="up" />
                      </Tooltip>
                    </PageActionItem>
                  ) : null}
                  {orderNumber !== totalInserts ? (
                    <PageActionItem onClick={() => swapInserts(orderNumber + 1)}>
                      <Tooltip
                        placement="bottom"
                        title={t(
                          INSERTS_TAB.INSERT_SWAP_NEXT.key,
                          INSERTS_TAB.INSERT_SWAP_NEXT.value,
                        )}
                      >
                        <Icon type="down" />
                      </Tooltip>
                    </PageActionItem>
                  ) : null}
                  <PageActionItem>
                    <Tooltip
                      placement="bottom"
                      title={t(
                        INSERTS_TAB.DELETE_INSERT_LABEL.key,
                        INSERTS_TAB.DELETE_INSERT_LABEL.value,
                      )}
                    >
                      <Popconfirm
                        placement="top"
                        trigger="click"
                        onConfirm={handleDelete}
                        title={t(
                          INSERTS_TAB.DELETE_TEMPLATE_INSERT_QUESTION_LABEL.key,
                          INSERTS_TAB.DELETE_TEMPLATE_INSERT_QUESTION_LABEL.value,
                        )}
                        okText={t(INSERTS_TAB.YES_LABEL.key, INSERTS_TAB.YES_LABEL.value)}
                        cancelText={t(INSERTS_TAB.NO_LABEL.key, INSERTS_TAB.NO_LABEL.value)}
                        overlayStyle={{ width: 350 }}
                      >
                        <Icon type="delete" />
                      </Popconfirm>
                    </Tooltip>
                  </PageActionItem>
                  <PageActionItem>
                    <BusinessLogicOptionPopover
                      showBlModal={toggleBusinessLogicModal}
                      deleteBusinessLogic={deleteBusinessLogic}
                      insert={insert}
                      globalVariables={globalVariables}
                      t={t}
                    />
                  </PageActionItem>
                </>
              ) : null}
              <PageActionItem onClick={() => setShowPreview(true)}>
                <Tooltip
                  placement="bottom"
                  title={t(INSERTS_TAB.PREVIEW_LABEL.key, INSERTS_TAB.PREVIEW_LABEL.value)}
                >
                  <Icon type="eye" />
                </Tooltip>
              </PageActionItem>
            </div>
          </div>
        ) : null}
        {isPreviewModal ? (
          <PreviewModal
            insert={insert}
            totalPages={totalPages}
            scaling={scaling}
            currentStartPageIndex={currentStartPageIndex}
          />
        ) : (
          <img
            src={thumbnail}
            onError={handleThumbnailError}
            style={{ width: 794, margin: 'auto' }}
            alt={name}
          />
        )}
      </div>
      <InsertPreviewModal
        visible={showPreview}
        onCancel={() => setShowPreview(false)}
        insert={insert}
      />
    </InsertPageWrapper>
  );
};

export const PdfInserts = ({ totalPages, lastPage }) => {
  const { t } = useTranslation(INSERT_NAMESPACES);
  const idSelector = useCallback(
    store => ({
      templateId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      templateInserts: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS],
      globalVariables: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    }),
    [],
  );
  const [{ templateId, templateInserts, globalVariables }, methods] = useDocumentContext(
    idSelector,
  );

  const editableSelector = useCallback(
    store => ({
      editable: store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN],
      previewModal: store[CONTEXT_KEYS.PREVIEW_MODAL],
    }),
    [],
  );
  const [{ editable, previewModal: isPreviewModal }] = useArtboardContext(
    editableSelector,
    'ComplexArtboard',
  );

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.SCALING_SETTING], []);
  const [scaling] = useArtboardContext(customArtboardSelector, 'ComplexArtboard');

  const interactiveDesignSelector = useCallback(
    store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN],
    [],
  );
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexArtboard`);

  let currentStartPageIndex = lastPage;

  return (
    <>
      {templateInserts?.map((v, index) => {
        currentStartPageIndex =
          index === 0 ? currentStartPageIndex : currentStartPageIndex + Number(v.pages);
        return (
          <PdfInsert
            insert={v}
            templateInserts={templateInserts}
            totalInserts={templateInserts?.length || 0}
            templateId={templateId}
            store={methods}
            globalVariables={globalVariables}
            editable={editable}
            isPreviewModal={isPreviewModal}
            totalPages={totalPages}
            scaling={scaling}
            currentStartPageIndex={currentStartPageIndex}
            t={t}
            isInteractiveDesign={isInteractiveDesign}
          />
        );
      })}
    </>
  );
};
